import type { Building } from "building-types/building/buildingTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { EntityGraph } from "common/graph/entityGraph";
import type { EntityRelationship } from "common/graph/entityGraphTypes";
import { worldInchesNew } from "common/utils/nominalNumbers";
import { docEntityHandleHash } from "doc/entity/core/handleHash";
import { aabb3Center, aabb3Copy, aabb3Correct, aabb3Grow, isAABB3Valid } from "geom/aabb3";
import type { AABB3 } from "geom/aabb3Types";
import type { Vec2 } from "geom/vec2";
import type { BBox, CompareEqual } from "rbush-3d";
import { RBush3D } from "rbush-3d";

const SEARCH_BUFFER = worldInchesNew(0.1);
const SEARCH_MIN_SIZE = worldInchesNew(2);

interface HandleAABB3 extends BBox {
  readonly handleHash: string;
}

function hashFunction(entity: DocEntityHandle): string {
  return docEntityHandleHash(entity);
}

function handleAABB3Equals(a: HandleAABB3, b: HandleAABB3): boolean {
  return a.handleHash === b.handleHash;
}

function defaultPredicate(_: DocEntityHandle): boolean {
  return true;
}

type HandleHash = string;

export type RelationshipType = "HOSTS" | "ADJACENT_TO";

export class EntityIndex {
  protected _handleBounds: Map<HandleHash, AABB3> | undefined = undefined;
  protected get handleBounds(): Map<HandleHash, AABB3> {
    if (!this._handleBounds) {
      return this.setNewHandleBounds();
    }
    return this._handleBounds;
  }
  protected _handleAABBs: Map<HandleHash, HandleAABB3> | undefined = undefined;
  protected get handleAABBs(): Map<HandleHash, HandleAABB3> {
    if (!this._handleAABBs) {
      return this.setNewHandleAABBs();
    }
    return this._handleAABBs;
  }
  protected _index: RBush3D | undefined = undefined;
  protected get index(): RBush3D {
    if (!this._index) {
      return this.setNewIndex();
    }
    return this._index;
  }
  protected _graph: EntityGraph<DocEntityHandle, RelationshipType> | undefined = undefined;
  get graph(): EntityGraph<DocEntityHandle, RelationshipType> {
    if (!this._graph) {
      return this.setNewGraph();
    }
    return this._graph;
  }

  setNewHandleBounds() {
    this._handleBounds = new Map();
    return this._handleBounds;
  }

  setNewHandleAABBs() {
    this._handleAABBs = new Map<HandleHash, HandleAABB3>();
    return this._handleAABBs;
  }

  setNewGraph() {
    this._graph = new EntityGraph<DocEntityHandle, RelationshipType>(hashFunction);
    return this._graph;
  }

  setNewIndex() {
    this._index = new RBush3D();
    return this._index;
  }

  reset() {
    this.setNewHandleBounds();
    this.setNewHandleAABBs();
    this.setNewGraph();
    this.setNewIndex();
  }

  nodes(): DocEntityHandle[] {
    return this.graph.nodes;
  }

  nodeAABB(handle: DocEntityHandle): AABB3 | undefined {
    return this.handleAABBs.get(this.nodeHash(handle));
  }

  relationships(
    node: DocEntityHandle,
    predicate?: (relationship: EntityRelationship<string, RelationshipType>) => boolean,
  ): EntityRelationship<string, RelationshipType>[] {
    return this.graph.relationships(node, predicate);
  }

  nodeHash(node: DocEntityHandle): HandleHash {
    return hashFunction(node);
  }

  addNode(node: DocEntityHandle, aabb?: AABB3): DocEntityHandle {
    const nodeHash = this.nodeHash(node);
    const byHash = this.nodeByHash(nodeHash);
    if (!byHash) {
      node = this.graph.add(node);
    }
    // a node may or may not have an aabb associated with it
    if (isAABB3Valid(aabb)) {
      const nodeBox: HandleAABB3 = {
        ...aabb3Correct(aabb),
        handleHash: nodeHash,
      };
      this.index.remove(nodeBox, handleAABB3Equals as unknown as CompareEqual);
      this.handleAABBs.set(nodeHash, nodeBox);
      this.index.insert(nodeBox);
    }

    return node;
  }

  deleteNode(node: DocEntityHandle): void {
    const nodeHash = this.nodeHash(node);
    if (this.handleAABBs.has(nodeHash)) {
      this.index.remove(this.handleAABBs.get(nodeHash));
      this.handleAABBs.delete(nodeHash);
    }
    this.graph.delete(node);
  }

  relate(a: DocEntityHandle, type: RelationshipType, b: DocEntityHandle, bidirectional: boolean = true): string {
    return this.graph.relate(a, type, b, bidirectional);
  }

  nodeByDocEntityHandle(handle: DocEntityHandle): DocEntityHandle | undefined {
    return this.graph.node(handle);
  }

  nodeByHash(hash: string): DocEntityHandle | undefined {
    return this.graph.nodeByHash(hash);
  }

  loadBuildingEntityGraph(building: Pick<Building, "entityGraphNodes" | "entityGraphRelationships">) {
    building.entityGraphNodes.forEach((n) => this.addNode(n.handle, n.aabb));
    building.entityGraphRelationships.forEach((r) => {
      const a = this.nodeByDocEntityHandle(r.a.handle);
      const b = this.nodeByDocEntityHandle(r.b.handle);
      const t = r.type as RelationshipType;
      if (a && b) {
        this.relate(a, t, b);
      } else {
        console.warn("failed to relate", r.a, r.b, r.type);
      }
    });
  }

  /**
   * Breadth-first search of the graph, stopping at the provided depth.
   * Returns an array of all visited nodes.
   *
   * @param root - The node to start the search from
   * @param depth - The maximum depth to search (0 = only root, 1 = root + immediate neighbors, etc.)
   * @returns Array of all unique nodes visited during the search
   */
  breadthFirstSearch(root: DocEntityHandle, depth: number = 1): DocEntityHandle[] {
    const rootNode = this.nodeByDocEntityHandle(root);
    if (!rootNode) {
      return [];
    }
    return this.graph.breadthFirstSearch(rootNode, depth);
  }

  spatialSearch(search: BBox, predicate: (handle: DocEntityHandle) => boolean = defaultPredicate): DocEntityHandle[] {
    const neighbors = this.index.search(search) as HandleAABB3[];

    // NOTE: this is the only place where we remove nodes from the spatial index
    for (const neighbor of neighbors) {
      if (!this.graph.nodeByHash(neighbor.handleHash)) {
        this.index.remove(neighbor);
      }
    }

    return neighbors
      .map((n) => this.graph.nodeByHash(n.handleHash))
      .filter(notUndefined)
      .filter(predicate);
  }
}

function notUndefined<T>(item: T | undefined): item is T {
  return item !== undefined;
}

export function searchDomainAbove(aabb3: AABB3, searchDistance: number = 12, buffer: number = SEARCH_BUFFER): BBox {
  aabb3Correct(aabb3);
  const [, , z] = aabb3Center(aabb3);
  const [minX, maxX] = shrinkDomain([aabb3.minX, aabb3.maxX], buffer, SEARCH_MIN_SIZE);
  const [minY, maxY] = shrinkDomain([aabb3.minY, aabb3.maxY], buffer, SEARCH_MIN_SIZE);
  return {
    minX,
    minY,
    minZ: z,
    maxX,
    maxY,
    maxZ: aabb3.maxZ + searchDistance,
  };
}

export function searchDomainBelow(aabb3: AABB3, searchDistance: number = 12, buffer: number = SEARCH_BUFFER): BBox {
  const [, , z] = aabb3Center(aabb3);
  const [minX, maxX] = shrinkDomain([aabb3.minX, aabb3.maxX], buffer, SEARCH_MIN_SIZE);
  const [minY, maxY] = shrinkDomain([aabb3.minY, aabb3.maxY], buffer, SEARCH_MIN_SIZE);
  return {
    minX,
    minY,
    minZ: aabb3.minZ - searchDistance,
    maxX,
    maxY,
    maxZ: z,
  };
}

export function searchDomainAround(aabb3: AABB3, searchDistance: number = 12): BBox {
  const aabb = aabb3Copy(aabb3);
  aabb3Grow(aabb, searchDistance, searchDistance, searchDistance);
  return aabb;
}

/**
 * Attempts to shrink a domain by a given distance, but only if the domain is large enough.
 */
function shrinkDomain([min, max]: Vec2, shrinkEachSideBy: number, minSize: number = 0): [number, number] {
  const size = max - min;
  if (minSize > 0 && size < minSize) {
    const halfway = size / 2 + min;
    return [halfway - minSize / 2, halfway + minSize / 2];
  }
  if (size > shrinkEachSideBy * 2 + minSize) {
    return [min + shrinkEachSideBy, max - shrinkEachSideBy];
  }
  return [min, max]; // domain is too small to shrink
}
